import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { 
  Church, 
  User, 
  Mail, 
  Lock, 
  Users, 
  Sparkles, 
  ArrowRight, 
  ArrowLeft,
  Check,
  Loader2,
  AlertCircle,
  Camera,
  X,
  Cake,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';
import { IMAGE_ACCEPT, isImageFile } from '@/lib/imageFiles';
import LocalImageThumb from '@/components/shared/LocalImageThumb';
import { useDesign } from '@/contexts/DesignContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pricing tiers
const PRICING_TIERS = {
  starter: { name: 'Starter', monthly: 11, annual: 110, users: 15, aiChats: 20, admins: 3 },
  growth: { name: 'Growth', monthly: 22, annual: 220, users: 25, aiChats: 50, admins: 3 },
  pro: { name: 'Pro', monthly: 33, annual: 330, users: 50, aiChats: 80, admins: 3 },
  mega: { name: 'Mega', monthly: 55, annual: 550, users: 100, aiChats: 120, admins: 3 },
  ultra: { name: 'Ultra', monthly: 99, annual: 990, users: 500, aiChats: 250, admins: 15, hidden: true }
};

const TIER_ORDER = ['starter', 'growth', 'pro', 'mega'];
const HIDDEN_TIER_ORDER = ['ultra'];

function ChurchSignupPage({ onBack }) {
  const { isStudio } = useDesign();
  const [step, setStep] = useState(1); // 1: Details, 2: Plan Selection

  // URL-param detection: ?tier=ultra reveals the hidden Ultra card and
  // pre-selects it. Also accepts ?billing=monthly|annual to pre-select cycle.
  // Reads from window.__bootQuery (captured at app boot in App.js) — needed
  // because auth redirects can wipe live window.location.search before this
  // component mounts.
  const _readBootParam = (key) => {
    if (typeof window === 'undefined') return '';
    const boot = window.__bootQuery || {};
    if (boot[key]) return String(boot[key]).toLowerCase();
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get(key) || '').toLowerCase();
    } catch (e) { return ''; }
  };
  const [revealedHiddenTiers] = useState(() => {
    const tier = _readBootParam('tier') || _readBootParam('plan');
    if (tier && HIDDEN_TIER_ORDER.includes(tier)) return [tier];
    return [];
  });
  const initialTierFromUrl = revealedHiddenTiers[0] || (() => {
    const t = _readBootParam('tier') || _readBootParam('plan');
    return TIER_ORDER.includes(t) ? t : null;
  })();
  const initialBillingCycle = (() => {
    const c = _readBootParam('billing');
    return c === 'annual' || c === 'monthly' ? c : 'monthly';
  })();

  const [formData, setFormData] = useState({
    churchName: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    birthdayMonth: '',
    birthdayDay: '',
    tier: initialTierFromUrl || 'starter',
    billingCycle: initialBillingCycle
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = React.useRef(null);

  const handlePictureSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isImageFile(file)) {
      setErrors(prev => ({ ...prev, profilePicture: 'Please select an image file' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, profilePicture: 'Image File Size too big. Must be under 5MB' }));
      return;
    }
    setProfilePicture(file);
    setErrors(prev => { const { profilePicture: _, ...rest } = prev; return rest; });
    // A HEIC preview only draws in Safari; LocalImageThumb shows a label
    // elsewhere (the base64 goes through the server's JPEG conversion).
    const reader = new FileReader();
    reader.onloadend = () => setProfilePicturePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0]?.[0]?.toUpperCase() || '?';
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.churchName.trim()) {
      newErrors.churchName = 'Church name is required';
    }
    if (!formData.adminName.trim()) {
      newErrors.adminName = 'Your name is required';
    }
    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Please enter a valid email';
    }
    if (!formData.adminPassword) {
      newErrors.adminPassword = 'Password is required';
    } else if (formData.adminPassword.length < 6) {
      newErrors.adminPassword = 'Password must be at least 6 characters';
    }
    if (formData.adminPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.birthdayMonth || !formData.birthdayDay) {
      newErrors.birthday = 'Birthday is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setApiError('');

    try {
      // Convert profile picture to base64 if selected
      let profilePictureBase64 = null;
      if (profilePicture) {
        profilePictureBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(profilePicture);
        });
      }

      const response = await fetch(`${API_URL}/api/stripe/signup-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          church_name: formData.churchName,
          admin_name: formData.adminName,
          admin_email: formData.adminEmail.toLowerCase(),
          admin_password: formData.adminPassword,
          admin_birthday: `${formData.birthdayMonth.padStart(2, '0')}-${formData.birthdayDay.padStart(2, '0')}`,
          admin_profile_picture: profilePictureBase64,
          tier: formData.tier,
          billing_cycle: formData.billingCycle,
          origin_url: window.location.origin
        })
      });

      const data = await response.clone().json();

      if (response.ok && data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        setApiError(data.detail || 'Failed to start signup. Please try again.');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setApiError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedTier = PRICING_TIERS[formData.tier];
  const price = formData.billingCycle === 'annual' ? selectedTier.annual : selectedTier.monthly;

  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  // ---- Studio (doc 119 §24): the 64 header, the 440 card on step 1, the
  // 1040 plan column on step 2. Every control, guard and testid is classic's.
  if (isStudio) {
    const sField = (label, inner, err, help) => (
      <div className="st-au-field">
        <label>{label}</label>
        {inner}
        {help && <div className="st-au-fhelp">{help}</div>}
        {err && <div className="st-au-ferr">{err}</div>}
      </div>
    );
    const leadInput = (Icon, props, hasErr, trail) => (
      <div className="st-au-fwrap">
        <Icon className="st-au-leadicon" />
        <Input {...props} className={`st-au-in st-au-lead${trail ? ' st-au-trail' : ''}${hasErr ? ' st-au-bad-b' : ''}`} />
        {trail}
      </div>
    );
    const eye = (shown, toggle, testid) => (
      <button type="button" onClick={toggle} className="st-au-eye" tabIndex={-1} data-testid={testid}>
        {shown ? <EyeOff /> : <Eye />}
      </button>
    );
    const planCard = (tierId, wide) => {
      const tier = PRICING_TIERS[tierId];
      const isSelected = formData.tier === tierId;
      const tierPrice = formData.billingCycle === 'annual' ? tier.annual : tier.monthly;
      const head = (
        <div className="st-au-planhead">
          <div className="st-au-nm">{tier.name}</div>
          <div className="st-au-pr"><b>${tierPrice}</b><span>/{formData.billingCycle === 'annual' ? 'yr' : 'mo'}</span></div>
          {formData.billingCycle === 'annual' && (
            <div className="st-au-bl">${Math.round(tier.annual / 12)}/mo, billed yearly</div>
          )}
        </div>
      );
      return (
        <div
          key={tierId}
          data-testid={`tier-card-${tierId}`}
          className={`st-au-plan${isSelected ? ' st-au-on' : ''}${wide ? ' st-au-wide' : ''}`}
          onClick={() => setFormData({ ...formData, tier: tierId })}
        >
          {head}
          <ul>
            <li><Users />{tier.users} team members</li>
            <li><Sparkles />{tier.aiChats} AI chats a month</li>
            <li><Shield />{tier.admins} admin seats</li>
          </ul>
        </div>
      );
    };

    return (
      <div className="st-au-shell">
        <div className="st-au-hdr">
          <div data-ui="brand-mark" aria-label="Worship OS" className="st-au-mark">W</div>
          <span className="st-au-wm">Worship OS</span>
          {onBack && (
            <span className="st-au-right">
              <Button variant="ghost" onClick={onBack} className="st-au-btn st-au-ghost">
                <ArrowLeft />
                Back to sign in
              </Button>
            </span>
          )}
        </div>

        {step === 1 ? (
          <div className="st-au-page">
            <div className="st-au-card">
              <div className="st-au-head">
                <div className="st-au-titlerow">
                  <h1 className="st-au-t">Create your church account</h1>
                  <span className="st-au-step">1 of 2</span>
                </div>
                <p className="st-au-s">Start your 14-day free trial</p>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="st-au-form">
                {sField('Church name', leadInput(Church, {
                  id: 'churchName', 'data-testid': 'church-name-input', placeholder: 'Grace Community Church',
                  value: formData.churchName, onChange: (e) => setFormData({ ...formData, churchName: e.target.value }),
                }, !!errors.churchName), errors.churchName)}

                {sField('Your name', leadInput(User, {
                  id: 'adminName', 'data-testid': 'admin-name-input', placeholder: 'John Smith',
                  value: formData.adminName, onChange: (e) => setFormData({ ...formData, adminName: e.target.value }),
                }, !!errors.adminName), errors.adminName)}

                {sField('Email address', leadInput(Mail, {
                  id: 'adminEmail', 'data-testid': 'admin-email-input', type: 'email', placeholder: 'john@church.com',
                  value: formData.adminEmail, onChange: (e) => setFormData({ ...formData, adminEmail: e.target.value }),
                }, !!errors.adminEmail), errors.adminEmail)}

                {sField('Password', leadInput(Lock, {
                  id: 'adminPassword', 'data-testid': 'admin-password-input',
                  type: showPassword ? 'text' : 'password', placeholder: '••••••••',
                  value: formData.adminPassword, onChange: (e) => setFormData({ ...formData, adminPassword: e.target.value }),
                }, !!errors.adminPassword, eye(showPassword, () => setShowPassword(p => !p), 'signup-password-toggle')),
                  errors.adminPassword, 'At least 6 characters.')}

                {sField('Confirm password', leadInput(Lock, {
                  id: 'confirmPassword', 'data-testid': 'confirm-password-input',
                  type: showConfirmPassword ? 'text' : 'password', placeholder: '••••••••',
                  value: formData.confirmPassword, onChange: (e) => setFormData({ ...formData, confirmPassword: e.target.value }),
                }, !!errors.confirmPassword, eye(showConfirmPassword, () => setShowConfirmPassword(p => !p), 'signup-confirm-password-toggle')),
                  errors.confirmPassword)}

                <div className="st-au-field">
                  <label>Profile picture (optional)</label>
                  <div className="st-au-avrow">
                    <div className="st-au-avwrap" onClick={() => fileInputRef.current?.click()}>
                      {profilePicturePreview ? (
                        <div className="st-au-photo">
                          <LocalImageThumb src={profilePicturePreview} name={profilePicture?.name} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="st-au-av"><span>{getInitials(formData.adminName)}</span></div>
                      )}
                      <span className="st-au-scrim"><Camera className="w-[18px] h-[18px]" /></span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={IMAGE_ACCEPT}
                        onChange={handlePictureSelect}
                        className="hidden"
                        data-testid="church-signup-avatar-input"
                      />
                    </div>
                    <div>
                      {profilePicturePreview ? (
                        <div className="flex items-center gap-1.5">
                          <span className="st-au-okline">Photo selected</span>
                          <button type="button" onClick={() => { setProfilePicture(null); setProfilePicturePreview(null); }} className="st-au-xbtn" aria-label="Remove photo">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="st-au-n">Click to upload</div>
                          <div className="st-au-fhelp">Max 5 MB</div>
                        </>
                      )}
                    </div>
                  </div>
                  {errors.profilePicture && <div className="st-au-ferr">{errors.profilePicture}</div>}
                </div>

                <div className="st-au-field">
                  <label>Birthday</label>
                  <div className="st-au-bday">
                    <select
                      data-testid="church-signup-birthday-month"
                      value={formData.birthdayMonth}
                      onChange={(e) => setFormData({ ...formData, birthdayMonth: e.target.value })}
                      className={`${errors.birthday ? 'st-au-bad-b' : ''}${formData.birthdayMonth ? '' : ' st-au-ph'}`}
                    >
                      <option value="">Month</option>
                      {MONTHS.map((m, i) => (<option key={i} value={String(i + 1)}>{m}</option>))}
                    </select>
                    <select
                      data-testid="church-signup-birthday-day"
                      value={formData.birthdayDay}
                      onChange={(e) => setFormData({ ...formData, birthdayDay: e.target.value })}
                      className={`${errors.birthday ? 'st-au-bad-b' : ''}${formData.birthdayDay ? '' : ' st-au-ph'}`}
                    >
                      <option value="">Day</option>
                      {Array.from({length: 31}, (_, i) => (<option key={i} value={String(i + 1)}>{i + 1}</option>))}
                    </select>
                  </div>
                  {errors.birthday && <div className="st-au-ferr">{errors.birthday}</div>}
                </div>

                <Button data-testid="continue-to-plan-btn" type="submit" className="st-au-btn st-au-pri st-au-lg st-au-full">
                  Continue to plan selection
                </Button>

                <div className="st-au-foot">
                  Already have an account?{' '}
                  <button type="button" onClick={onBack} className="st-au-lnk">Sign in</button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="st-au-page">
            <div className="st-au-planwrap">
              <div className="st-au-head st-au-c">
                <div className="st-au-titlerow">
                  <h1 className="st-au-t">Choose your plan</h1>
                  <span className="st-au-step">2 of 2</span>
                </div>
                <p className="st-au-s">Start with a 14-day free trial. Cancel anytime.</p>
              </div>

              <div className="flex items-center gap-2.5">
                <div data-ui="segmented" className="inline-flex items-center">
                  <button
                    className="st-au-segbtn"
                    data-testid="monthly-toggle"
                    data-active={formData.billingCycle === 'monthly'}
                    onClick={() => setFormData({ ...formData, billingCycle: 'monthly' })}
                  >
                    Monthly
                  </button>
                  <button
                    className="st-au-segbtn"
                    data-testid="annual-toggle"
                    data-active={formData.billingCycle === 'annual'}
                    onClick={() => setFormData({ ...formData, billingCycle: 'annual' })}
                  >
                    Annual
                  </button>
                </div>
                <span className="st-au-segcap">Annual saves 2 months</span>
              </div>

              <div className="st-au-plangrid">{TIER_ORDER.map((tierId) => planCard(tierId, false))}</div>

              {revealedHiddenTiers.includes('ultra') && planCard('ultra', true)}

              {apiError && (
                <div style={{ width: '440px', maxWidth: '100%' }}>
                  <div className="st-au-notice st-au-bad" role="alert">
                    <AlertCircle className="w-4 h-4" />
                    <span>{apiError}</span>
                  </div>
                </div>
              )}

              <div className="st-au-actions">
                <Button data-testid="back-to-details-btn" variant="outline" onClick={() => setStep(1)} className="st-au-btn st-au-ghost st-au-edge">
                  <ArrowLeft />
                  Back
                </Button>
                <span className="st-au-sp" />
                <Button
                  data-testid="start-trial-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`st-au-btn st-au-pri st-au-lg${loading ? ' st-au-busy' : ''}`}
                >
                  {loading ? (
                    <><span className="st-au-spin" /><span>Processing…</span></>
                  ) : (
                    <span>{`Start 14-day free trial · $${price}/${formData.billingCycle === 'annual' ? 'yr' : 'mo'}`}</span>
                  )}
                </Button>
              </div>

              <div className="st-au-trial">
                Your card will not be charged during the trial.<br />
                After 14 days, you&apos;ll be charged ${price}/{formData.billingCycle === 'annual' ? 'year' : 'month'} unless you cancel.
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 dark:from-indigo-950/40 via-white dark:via-[#1e1f22] to-purple-50">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-white/10 bg-white/80 dark:bg-[#2d2f33]/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div data-ui="brand-mark" className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Church className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-slate-100">Worship OS</span>
          </div>
          {onBack && (
            <Button variant="ghost" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step >= 1 ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-[#3e4145] text-slate-500 dark:text-slate-400'
          }`}>
            {step > 1 ? <Check className="w-4 h-4" /> : '1'}
          </div>
          <div className={`w-24 h-1 rounded ${step >= 2 ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-[#3e4145]'}`} />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
            step >= 2 ? 'bg-indigo-600 text-white' : 'bg-slate-200 dark:bg-[#3e4145] text-slate-500 dark:text-slate-400'
          }`}>
            2
          </div>
        </div>

        {/* Step 1: Church & Admin Details */}
        {step === 1 && (
          <Card className="max-w-lg mx-auto shadow-xl border-0">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl">Create Your Church Account</CardTitle>
              <CardDescription>Start your 14-day free trial</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="space-y-4">
              {/* Church Name */}
              <div className="space-y-2">
                <Label htmlFor="churchName">Church Name</Label>
                <div className="relative">
                  <Church className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  <Input
                    id="churchName"
                    data-testid="church-name-input"
                    placeholder="Grace Community Church"
                    value={formData.churchName}
                    onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
                    className={`pl-10 h-11 ${errors.churchName ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.churchName && (
                  <p className="text-sm text-red-500 dark:text-red-400">{errors.churchName}</p>
                )}
              </div>

              {/* Admin Name */}
              <div className="space-y-2">
                <Label htmlFor="adminName">Your Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  <Input
                    id="adminName"
                    data-testid="admin-name-input"
                    placeholder="John Smith"
                    value={formData.adminName}
                    onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                    className={`pl-10 h-11 ${errors.adminName ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.adminName && (
                  <p className="text-sm text-red-500 dark:text-red-400">{errors.adminName}</p>
                )}
              </div>

              {/* Admin Email */}
              <div className="space-y-2">
                <Label htmlFor="adminEmail">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  <Input
                    id="adminEmail"
                    data-testid="admin-email-input"
                    type="email"
                    placeholder="john@church.com"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    className={`pl-10 h-11 ${errors.adminEmail ? 'border-red-500' : ''}`}
                  />
                </div>
                {errors.adminEmail && (
                  <p className="text-sm text-red-500 dark:text-red-400">{errors.adminEmail}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="adminPassword">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  <Input
                    id="adminPassword"
                    data-testid="admin-password-input"
                    type={showPassword ? "text" : "password"}
                    placeholder="At least 6 characters"
                    value={formData.adminPassword}
                    onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                    className={`pl-10 pr-10 h-11 ${errors.adminPassword ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    tabIndex={-1}
                    data-testid="signup-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.adminPassword && (
                  <p className="text-sm text-red-500 dark:text-red-400">{errors.adminPassword}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={`pl-10 pr-10 h-11 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    tabIndex={-1}
                    data-testid="signup-confirm-password-toggle"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-red-500 dark:text-red-400">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Profile Picture (Optional) */}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200 font-medium flex items-center gap-2">
                  <Camera className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  Profile Picture <span className="text-slate-400 dark:text-slate-500 text-xs">(optional)</span>
                </Label>
                <div className="flex items-center gap-4">
                  <div 
                    className="relative cursor-pointer group"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {profilePicturePreview ? (
                      <div className="w-14 h-14 rounded-xl overflow-hidden border-2 border-indigo-500 shadow-lg">
                        <LocalImageThumb src={profilePicturePreview} name={profilePicture?.name} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center border-2 border-slate-200 dark:border-white/10">
                        <span className="text-lg font-bold text-white">{getInitials(formData.adminName)}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center">
                      <Camera className="w-4 h-4 text-white" />
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={IMAGE_ACCEPT}
                      onChange={handlePictureSelect}
                      className="hidden"
                      data-testid="church-signup-avatar-input"
                    />
                  </div>
                  <div className="flex-1">
                    {profilePicturePreview ? (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-green-600 dark:text-green-400">Photo selected</span>
                        <button type="button" onClick={() => { setProfilePicture(null); setProfilePicturePreview(null); }} className="text-slate-400 dark:text-slate-500 hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Click to upload</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500">Max 5MB</p>
                      </div>
                    )}
                  </div>
                </div>
                {errors.profilePicture && <p className="text-sm text-red-500 dark:text-red-400">{errors.profilePicture}</p>}
              </div>

              {/* Birthday (Required) */}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-200 font-medium flex items-center gap-2">
                  <Cake className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  Birthday <span className="text-red-500 dark:text-red-400">*</span>
                </Label>
                <div className="flex gap-3">
                  <select
                    data-testid="church-signup-birthday-month"
                    value={formData.birthdayMonth}
                    onChange={(e) => setFormData({ ...formData, birthdayMonth: e.target.value })}
                    className={`flex-1 h-11 px-3 rounded-md border ${errors.birthday ? 'border-red-500' : 'border-slate-200 dark:border-white/10'} bg-card text-sm`}
                  >
                    <option value="">Month</option>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                      <option key={i} value={String(i + 1)}>{m}</option>
                    ))}
                  </select>
                  <select
                    data-testid="church-signup-birthday-day"
                    value={formData.birthdayDay}
                    onChange={(e) => setFormData({ ...formData, birthdayDay: e.target.value })}
                    className={`w-24 h-11 px-3 rounded-md border ${errors.birthday ? 'border-red-500' : 'border-slate-200 dark:border-white/10'} bg-card text-sm`}
                  >
                    <option value="">Day</option>
                    {Array.from({length: 31}, (_, i) => (
                      <option key={i} value={String(i + 1)}>{i + 1}</option>
                    ))}
                  </select>
                </div>
                {errors.birthday && <p className="text-sm text-red-500 dark:text-red-400">{errors.birthday}</p>}
              </div>

              <Button 
                data-testid="continue-to-plan-btn"
                type="submit"
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700"
              >
                Continue to Plan Selection
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                Already have an account?{' '}
                <button 
                  type="button"
                  onClick={onBack} 
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200 font-medium"
                >
                  Sign in
                </button>
              </p>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Plan Selection */}
        {step === 2 && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-2">Choose Your Plan</h2>
              <p className="text-slate-500 dark:text-slate-400">Start with a 14-day free trial. Cancel anytime.</p>
            </div>

            {/* Billing Cycle Toggle */}
            <div className="flex justify-center">
              <div className="inline-flex items-center bg-card rounded-lg p-1 shadow-sm border border-slate-200 dark:border-white/10">
                <button
                  data-testid="monthly-toggle"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    formData.billingCycle === 'monthly'
                      ? 'bg-indigo-600 text-white shadow'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                  }`}
                  onClick={() => setFormData({ ...formData, billingCycle: 'monthly' })}
                >
                  Monthly
                </button>
                <button
                  data-testid="annual-toggle"
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    formData.billingCycle === 'annual'
                      ? 'bg-indigo-600 text-white shadow'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                  }`}
                  onClick={() => setFormData({ ...formData, billingCycle: 'annual' })}
                >
                  Annual (Save 2 months)
                </button>
              </div>
            </div>

            {/* Plan Cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {TIER_ORDER.map((tierId) => {
                const tier = PRICING_TIERS[tierId];
                const isSelected = formData.tier === tierId;
                const tierPrice = formData.billingCycle === 'annual' ? tier.annual : tier.monthly;
                
                return (
                  <Card
                    key={tierId}
                    data-testid={`tier-card-${tierId}`}
                    className={`cursor-pointer transition-all ${
                      isSelected
                        ? 'ring-2 ring-indigo-500 dark:ring-indigo-400 shadow-lg border-indigo-200 dark:border-indigo-900'
                        : 'hover:shadow-md border-slate-200 dark:border-white/10'
                    }`}
                    onClick={() => setFormData({ ...formData, tier: tierId })}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-lg">
                          {tier.name}
                        </h3>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                          ${tierPrice}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400 text-sm">
                          /{formData.billingCycle === 'annual' ? 'year' : 'month'}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                          <Users className="w-4 h-4 text-indigo-500" />
                          {tier.users} team members
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                          <Sparkles className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                          {tier.aiChats} AI chats/mo
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                          <Shield className="w-4 h-4 text-indigo-500" />
                          {tier.admins} admin seats
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Ultra (hidden) — only rendered when revealed via ?tier=ultra URL param.
                Compact horizontal layout: ~half the height of the standard cards above. */}
            {revealedHiddenTiers.includes('ultra') && (() => {
              const tier = PRICING_TIERS.ultra;
              const isSelected = formData.tier === 'ultra';
              const tierPrice = formData.billingCycle === 'annual' ? tier.annual : tier.monthly;
              return (
                <button
                  type="button"
                  data-testid="tier-card-ultra"
                  onClick={() => setFormData({ ...formData, tier: 'ultra' })}
                  className={`w-full text-left rounded-xl overflow-hidden transition-all bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 dark:from-amber-950/40 dark:via-orange-950/40 dark:to-rose-950/40 ${
                    isSelected
                      ? 'ring-2 ring-amber-400 dark:ring-amber-300 shadow-md'
                      : 'hover:shadow-md ring-1 ring-amber-200/60 dark:ring-amber-900/40'
                  }`}
                >
                  <div className="flex items-center gap-3 sm:gap-5 px-4 py-3">
                    {/* Name + selection indicator */}
                    <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
                      <h3 className="font-bold text-lg sm:text-xl bg-gradient-to-r from-amber-700 to-rose-700 dark:from-amber-300 dark:to-rose-300 bg-clip-text text-transparent">
                        {tier.name}
                      </h3>
                      {isSelected && (
                        <div className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center flex-shrink-0">
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Price */}
                    <div className="flex-shrink-0 whitespace-nowrap">
                      <span className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
                        ${tierPrice}
                      </span>
                      <span className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm">
                        /{formData.billingCycle === 'annual' ? 'yr' : 'mo'}
                      </span>
                    </div>

                    {/* Features — hidden on small screens, single line on sm+ */}
                    <div className="hidden sm:flex items-center gap-4 lg:gap-6 flex-1 text-xs lg:text-sm text-slate-700 dark:text-slate-200 min-w-0">
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <Users className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        {tier.users} team members
                      </div>
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        {tier.aiChats} AI chats/mo
                      </div>
                      <div className="flex items-center gap-1.5 whitespace-nowrap">
                        <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                        {tier.admins} admin seats
                      </div>
                    </div>
                  </div>
                  {/* Mobile-only feature row underneath */}
                  <div className="sm:hidden flex flex-wrap items-center gap-x-4 gap-y-1 px-4 pb-3 text-xs text-slate-700 dark:text-slate-200">
                    <div className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      {tier.users} members
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      {tier.aiChats} AI chats/mo
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      {tier.admins} admin seats
                    </div>
                  </div>
                </button>
              );
            })()}

            {/* Error Message */}
            {apiError && (
              <div className="max-w-lg mx-auto p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{apiError}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                data-testid="back-to-details-btn"
                variant="outline"
                onClick={() => setStep(1)}
                className="w-full sm:w-auto"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                data-testid="start-trial-btn"
                onClick={handleSubmit}
                disabled={loading}
                className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 px-8"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Start 14-Day Free Trial - ${price}/{formData.billingCycle === 'annual' ? 'yr' : 'mo'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            {/* Trial Info */}
            <div className="text-center text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
              <p>Your card will not be charged during the trial.</p>
              <p className="mt-1">After 14 days, you&apos;ll be charged ${price}/{formData.billingCycle === 'annual' ? 'year' : 'month'} unless you cancel.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ChurchSignupPage;
